/* eslint-disable */
/**
 * Stage 73.20p — Reimbursement Policy Master
 *
 * Per-designation policy editor. Admin picks a designation, then adds
 * one row per expense category with limit + unit + bill requirement.
 * Saved policy is consumed by the reimbursement claim endpoint to
 * decide if a claim needs deviation approval.
 */
import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, Trash2, Save, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReimbursementPolicy() {
  const [selectedCompany] = useActiveCompany();
  // Stage 73.20y — mode toggle (designation | grade)
  const [mode, setMode] = useState('designation');
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [meta, setMeta] = useState({ categories: [], units: [] });
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [items, setItems] = useState([]);
  const [policyNotes, setPolicyNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, [selectedCompany, mode]);

  const loadAll = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const [d, g, p, m] = await Promise.all([
        axios.get(`${API}/masters/designations?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/masters/grades?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/reimbursement-policies?company_id=${selectedCompany}&mapping_key=${mode}`, { withCredentials: true }),
        axios.get(`${API}/reimbursement-policies/_meta`, { withCredentials: true }),
      ]);
      setDesignations(d.data || []);
      setGrades(g.data || []);
      setPolicies(p.data || []);
      setMeta(m.data || { categories: [], units: [] });
      setSelectedKeyId('');
      setItems([]);
      setPolicyNotes('');
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  // Items shown in the left panel — designations or grades, by mode.
  const keyItems = mode === 'grade' ? grades : designations;
  const keyLabelSingular = mode === 'grade' ? 'Grade' : 'Designation';

  const openPolicy = (keyId) => {
    setSelectedKeyId(keyId);
    const matchField = mode === 'grade' ? 'grade_id' : 'designation_id';
    const existing = policies.find(p => p[matchField] === keyId);
    setItems(existing?.items || []);
    setPolicyNotes(existing?.notes || '');
  };

  const addItem = () => {
    setItems([...items, {
      category: meta.categories[0] || '',
      subcategory: '',
      limit_amount: 0,
      limit_unit: 'per_claim',
      requires_bill: true,
      auto_approve_below: null,
      notes: '',
    }]);
  };

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const savePolicy = async () => {
    if (!selectedKeyId) return;
    if (items.length === 0) {
      toast.error('Add at least one policy item');
      return;
    }
    setSaving(true);
    try {
      const target = keyItems.find(d => d.id === selectedKeyId);
      const payload = {
        mapping_key: mode,
        company_id: selectedCompany,
        items: items.map(it => ({
          ...it,
          limit_amount: parseFloat(it.limit_amount) || 0,
          auto_approve_below: it.auto_approve_below === '' || it.auto_approve_below == null ? null : parseFloat(it.auto_approve_below),
        })),
        notes: policyNotes,
      };
      if (mode === 'grade') {
        payload.grade_id = selectedKeyId;
        payload.grade_name = target?.name || '';
      } else {
        payload.designation_id = selectedKeyId;
        payload.designation_name = target?.name || '';
      }
      await axios.post(`${API}/reimbursement-policies`, payload, { withCredentials: true });
      toast.success('Policy saved');
      await loadAll();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const deletePolicy = async () => {
    if (!selectedKeyId) return;
    if (!window.confirm(`Delete the policy for this ${keyLabelSingular.toLowerCase()}? All future claims will fall through to standard approval flow.`)) return;
    try {
      await axios.delete(`${API}/reimbursement-policies/${selectedKeyId}?mapping_key=${mode}`, { withCredentials: true });
      toast.success('Policy deleted');
      setItems([]);
      setPolicyNotes('');
      await loadAll();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="reimbursement-policy-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
              Reimbursement Policy Master
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Set expense caps per {keyLabelSingular.toLowerCase()}. Claims that exceed the cap are routed for special approval.
            </p>
          </div>
          {/* Mode toggle */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1" data-testid="policy-mode-toggle">
            <span className="text-xs font-semibold text-slate-600 px-2">Map policies by:</span>
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/payroll/policy-config?company_id=${selectedCompany}`,
                    { policy_mapping_mode: 'designation' }, { withCredentials: true });
                  setMode('designation');
                  toast.success('Switched policy mapping to designation');
                } catch (e) {
                  toast.error('Mode change failed: ' + (e?.response?.data?.detail || e.message));
                }
              }}
              data-testid="mode-designation-btn"
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${mode === 'designation' ? 'bg-white text-blue-700 shadow' : 'text-slate-600 hover:text-slate-900'}`}
            >Designation</button>
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/payroll/policy-config?company_id=${selectedCompany}`,
                    { policy_mapping_mode: 'grade' }, { withCredentials: true });
                  setMode('grade');
                  toast.success('Switched policy mapping to grade');
                } catch (e) {
                  toast.error('Mode change failed: ' + (e?.response?.data?.detail || e.message));
                }
              }}
              data-testid="mode-grade-btn"
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${mode === 'grade' ? 'bg-white text-blue-700 shadow' : 'text-slate-600 hover:text-slate-900'}`}
            >Grade</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left: designation OR grade list */}
          <Card className="p-3 lg:col-span-1">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">{keyLabelSingular}s</h3>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : keyItems.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                No {keyLabelSingular.toLowerCase()}s defined. Add them via Business Controls → {keyLabelSingular}s.
              </p>
            ) : (
              <ul className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid={`${mode}-list`}>
                {keyItems.map(d => {
                  const matchField = mode === 'grade' ? 'grade_id' : 'designation_id';
                  const hasPolicy = policies.find(p => p[matchField] === d.id);
                  return (
                    <li key={d.id}>
                      <button
                        onClick={() => openPolicy(d.id)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between hover:bg-blue-50 ${selectedKeyId === d.id ? 'bg-blue-100 font-medium' : ''}`}
                        data-testid={`${mode}-${d.id}`}
                      >
                        <span>{d.name}</span>
                        {hasPolicy && (
                          <span className="text-emerald-600 text-[10px] font-bold">{hasPolicy.items.length}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Right: policy editor */}
          <div className="lg:col-span-3 space-y-4">
            {!selectedKeyId ? (
              <Card className="p-8 text-center text-sm text-slate-500">
                Pick a {keyLabelSingular.toLowerCase()} from the left to define its reimbursement policy.
              </Card>
            ) : (
              <>
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700">
                      Policy items ({items.length})
                    </h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add Item
                      </Button>
                      <Button size="sm" onClick={savePolicy} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-policy-btn">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save Policy
                      </Button>
                      {policies.find(p => (mode === 'grade' ? p.grade_id : p.designation_id) === selectedKeyId) && (
                        <Button variant="outline" size="sm" onClick={deletePolicy} className="text-rose-600 border-rose-200">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {items.length === 0 ? (
                    <p className="text-xs text-slate-500 italic text-center py-6">
                      No policy items yet. Click &quot;Add Item&quot; to set the first cap.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-2 w-1/4">Category</th>
                            <th className="text-left p-2 w-1/5">Subcategory</th>
                            <th className="text-right p-2">Limit (₹)</th>
                            <th className="text-left p-2">Unit</th>
                            <th className="text-center p-2">Bill?</th>
                            <th className="text-right p-2">Auto-Approve Below</th>
                            <th className="p-2"></th>
                          </tr>
                        </thead>
                        <tbody data-testid="items-table">
                          {items.map((it, idx) => (
                            <tr key={idx} className="border-t border-slate-100" data-testid={`item-row-${idx}`}>
                              <td className="p-1">
                                <select
                                  value={it.category}
                                  onChange={e => updateItem(idx, { category: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs"
                                  data-testid={`item-category-${idx}`}
                                >
                                  {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="p-1">
                                <input
                                  value={it.subcategory || ''}
                                  onChange={e => updateItem(idx, { subcategory: e.target.value })}
                                  placeholder="e.g. Second AC"
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs"
                                />
                              </td>
                              <td className="p-1">
                                <input
                                  type="number"
                                  value={it.limit_amount}
                                  onChange={e => updateItem(idx, { limit_amount: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs text-right"
                                  data-testid={`item-limit-${idx}`}
                                />
                              </td>
                              <td className="p-1">
                                <select
                                  value={it.limit_unit}
                                  onChange={e => updateItem(idx, { limit_unit: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs"
                                >
                                  {meta.units.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </td>
                              <td className="p-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={it.requires_bill}
                                  onChange={e => updateItem(idx, { requires_bill: e.target.checked })}
                                />
                              </td>
                              <td className="p-1">
                                <input
                                  type="number"
                                  value={it.auto_approve_below || ''}
                                  onChange={e => updateItem(idx, { auto_approve_below: e.target.value })}
                                  placeholder="optional"
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs text-right"
                                />
                              </td>
                              <td className="p-1 text-right">
                                <button onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <label className="block mt-3 text-xs">
                    <span className="text-slate-700 font-medium">Notes</span>
                    <textarea
                      value={policyNotes}
                      onChange={e => setPolicyNotes(e.target.value)}
                      rows={2}
                      placeholder="e.g. Effective from FY 2026-27"
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                    />
                  </label>
                </Card>

                {/* Helper text */}
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-[11px] text-blue-900">
                  <p className="font-semibold mb-0.5">How this policy is enforced (current mode: {mode}):</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    <li>When an employee with this {keyLabelSingular.toLowerCase()} submits a reimbursement, the engine looks up the matching category here.</li>
                    <li>Within cap → claim follows the standard approval flow.</li>
                    <li>Within cap AND below &quot;Auto-Approve Below&quot; → claim is auto-approved on submission.</li>
                    <li>Above cap → claim flagged <b>needs_deviation_approval</b> and routed to special-approval inbox.</li>
                    <li>No matching category in policy → standard approval flow applies (no block).</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
